import { AlarmTagComponent, AlarmTagSeverity } from 'commons/components/alarm-tag/alarm-tag.component.tsx';
import { groupBy } from 'commons/utils/object.utils.ts';
import { qualifyLeafNames } from 'commons-app/utils/location-names.utils';
import { CellListElement, FieldConfig, TooltipedMoreIndicatorStyled } from 'commons/components';
import { TooltipedCustomFieldCell } from 'commons/components/table/components/TooltipedCustomFieldCell.component';
import { TFunction } from 'core/i18n/i18n';
import { Operator, OperatorLocation, TrainingEnrollmentStatus, TrainingSimple } from 'modules/operators/domain';
import { Stage, TrainingProgram } from 'modules/trainings/models';
import { OperatorsByStage } from 'modules/trainings/pods/private-pods/training-program-details/training-program-details.type';
import { AlarmBlankSpace, AlarmsCellWrapper, AlarmTooltipBody, AlarmTooltipTitle } from './format-table-cell.style';

const ALARM_TOOLTIP_MOUNT_DELAY_MS = 250;

const countOperatorsByStatus = (
    data: { status: string; numberOperators: number }[],
    statusesToCount: string[]
): number =>
    data.filter((item) => statusesToCount.includes(item.status)).reduce((acc, item) => acc + item.numberOperators, 0);

export const formatListField = <T extends { id: string; name: string }>(list: T[]): React.ReactNode => {
    if (!list || list.length === 0) {
        return <></>;
    }

    return (
        <CellListElement>
            <TooltipedCustomFieldCell value={list[0].name} />
            {list.length > 1 && (
                <CellListElement>
                    <TooltipedMoreIndicatorStyled
                        value={`+${list.length - 1}`}
                        tooltipText={list
                            .map((item) => item.name)
                            .slice(1)
                            .join(', ')}
                        showTooltipAlways
                    />
                </CellListElement>
            )}
        </CellListElement>
    );
};

const renderAlarm = (
    groupedAlarms: Partial<Record<string, TrainingEnrollmentStatus[]>>,
    t: TFunction,
    severity: keyof typeof groupedAlarms
) => (
    <AlarmTagComponent
        severity={severity as AlarmTagSeverity}
        size={groupedAlarms[severity]?.length ?? 0}
        mountDelayMs={ALARM_TOOLTIP_MOUNT_DELAY_MS}
    >
        <AlarmTooltipTitle>
            {severity === 'error' ? t('app.alarms.missed_stage') : t('app.alarms.due_soon')}:{' '}
        </AlarmTooltipTitle>
        <AlarmTooltipBody>{groupedAlarms[severity]?.map((data) => data?.name).join(', ')}</AlarmTooltipBody>
    </AlarmTagComponent>
);

const renderTrainingProgramDetailViewAlarm = (severity: string, t: TFunction) => (
    <AlarmTagComponent severity={severity as AlarmTagSeverity} mountDelayMs={ALARM_TOOLTIP_MOUNT_DELAY_MS}>
        {t(`commons.alarm-type.${severity}`)}
    </AlarmTagComponent>
);

const renderAlarmTpView = (count: number, severity: string, t: TFunction) => (
    <AlarmTagComponent severity={severity as AlarmTagSeverity} size={count} mountDelayMs={ALARM_TOOLTIP_MOUNT_DELAY_MS}>
        {t(`modules.trainings.pods.training-programs-list.table.alarms.${severity}`, { count })}
    </AlarmTagComponent>
);
export const formatAlarms = (op: Operator, t: TFunction) => {
    if (op.status.length > 0) {
        const groupedAlarms = groupBy(op.status, (item: TrainingEnrollmentStatus) => item.severity);
        return (
            <AlarmsCellWrapper>
                {groupedAlarms.error ? renderAlarm(groupedAlarms, t, 'error') : <AlarmBlankSpace />}
                {groupedAlarms.warning ? renderAlarm(groupedAlarms, t, 'warning') : <AlarmBlankSpace />}
            </AlarmsCellWrapper>
        );
    } else {
        return <></>;
    }
};

export const formatTrainingAlarms = (tp: TrainingProgram, t: TFunction) => {
    if (
        tp.stages?.some((s) => s.trainingEnrollmentsSummary.statusAndOperators.some((sao) => sao.numberOperators > 0))
    ) {
        const summary = tp.stages.flatMap((s) => s.trainingEnrollmentsSummary.statusAndOperators);
        const errorAlarmsCount = countOperatorsByStatus(summary, ['MISSED_STAGE']);
        const warningAlarmsCount = countOperatorsByStatus(summary, ['DUE_SOON']);

        return (
            <AlarmsCellWrapper>
                {errorAlarmsCount > 0 ? renderAlarmTpView(errorAlarmsCount, 'error', t) : <AlarmBlankSpace />}
                {warningAlarmsCount > 0 ? renderAlarmTpView(warningAlarmsCount, 'warning', t) : <AlarmBlankSpace />}
            </AlarmsCellWrapper>
        );
    } else {
        return <></>;
    }
};

export const formatStageAlarms = (stage: Stage, t: TFunction) => {
    if (stage.trainingEnrollmentsSummary.statusAndOperators.some((s) => s.numberOperators > 0)) {
        const summary = stage.trainingEnrollmentsSummary.statusAndOperators;
        const errorAlarmsCount = countOperatorsByStatus(summary, ['MISSED_STAGE']);
        const warningAlarmsCount = countOperatorsByStatus(summary, ['DUE_SOON']);

        return (
            <AlarmsCellWrapper>
                {errorAlarmsCount > 0 ? renderAlarmTpView(errorAlarmsCount, 'error', t) : <AlarmBlankSpace />}
                {warningAlarmsCount > 0 ? renderAlarmTpView(warningAlarmsCount, 'warning', t) : <AlarmBlankSpace />}
            </AlarmsCellWrapper>
        );
    } else {
        return <></>;
    }
};

export const formatTrainingProgramDetailViewAlarms = (op: OperatorsByStage, t: TFunction) => {
    if (op.trainingEnrollment.status === 'OK') {
        return <></>;
    }

    const hasErrorAlarm = ['MISSED_STAGE'].includes(op.trainingEnrollment.status);
    const hasWarningAlarm = ['DUE_SOON'].includes(op.trainingEnrollment.status);

    return (
        <AlarmsCellWrapper>
            {hasErrorAlarm && renderTrainingProgramDetailViewAlarm('error', t)}
            {hasWarningAlarm && renderTrainingProgramDetailViewAlarm('warning', t)}
        </AlarmsCellWrapper>
    );
};

export const OperatorNameColumnConfig = (
    t: TFunction,
    getHeaderColumn: (column: string) => string
): FieldConfig<Operator> => ({
    name: 'name',
    headerOptions: {
        text: t(getHeaderColumn('name')),
        sortable: true,
    },
    rowOptions: {
        cellOptions: {
            format: (op: Operator) => <TooltipedCustomFieldCell value={`${op.lastName}, ${op.firstName}`} />,
        },
    },
});

export const getBasicColumnConfig = (
    t: TFunction,
    getHeaderColumn: (column: string) => string,
    columnName: string,
    sortable: boolean = false
) => ({
    name: columnName,
    headerOptions: {
        text: t(getHeaderColumn(columnName)),
        sortable,
    },
});

export const OperatorLocationsLevel1ColumnConfig = (t: TFunction, getHeaderColumn: (column: string) => string) => ({
    name: 'locationsLevel1',
    headerOptions: {
        text: t(getHeaderColumn('locationsLevel1')),
    },
    rowOptions: {
        cellOptions: {
            format: (op: Operator) =>
                formatListField<Pick<OperatorLocation, 'id' | 'name'>>(
                    op.locations.map((root) => ({ id: root.id, name: root.name }))
                ),
        },
    },
});

export const OperatorLocationsLevel2ColumnConfig = (t: TFunction, getHeaderColumn: (column: string) => string) => ({
    name: 'locationsLevel2',
    headerOptions: {
        text: t(getHeaderColumn('locationsLevel2')),
    },
    rowOptions: {
        cellOptions: {
            format: (op: Operator) => {
                const leaves = op.locations.flatMap((root) =>
                    root.children.map((leaf) => ({ id: leaf.id, name: leaf.name, rootName: root.name }))
                );
                return formatListField<Pick<OperatorLocation, 'id' | 'name'>>(
                    qualifyLeafNames(leaves, op.locations.length > 1)
                );
            },
        },
    },
});

export const OperatorTrainingProgramsColumnConfig = (t: TFunction, getHeaderColumn: (column: string) => string) => ({
    name: 'trainingPrograms',
    headerOptions: {
        text: t(getHeaderColumn('trainingPrograms')),
    },
    rowOptions: {
        cellOptions: {
            format: (op: Operator) => formatListField<TrainingSimple>(op.trainingPrograms),
        },
    },
});

export const OperatorAlarmsColumnConfig = (
    t: TFunction,
    getHeaderColumn: (column: string) => string
): FieldConfig<Operator> => ({
    name: 'alarms',
    headerOptions: {
        text: t(getHeaderColumn('alarms')),
        width: '160px',
        sortable: true,
    },
    rowOptions: {
        cellOptions: {
            format: (op: Operator) => formatAlarms(op, t),
        },
    },
});
